import React, { useEffect, useMemo, useState } from 'react'
import { useGetGrowthWheelAssessmentComparisonQuery } from '../../redux/growth-wheel.endpoints'
import { GrowthWheelAssessmentSession } from '../../types/GrowthWheel'
import {
  buildPointGroups,
  getComparisonSessions,
  getInitialSessionIds,
} from './growthComparisonChartUtils'
import { GrowthComparisonChartCanvas } from './GrowthComparisonChartCanvas'
import { GrowthComparisonChartControls } from './GrowthComparisonChartControls'

interface Props {
  userId: number
  sessions: GrowthWheelAssessmentSession[]
  defaultSessionId: number | null
  scoreField: 'selfLevel' | 'leaderLevel'
}

export function GrowthComparisonChart({
  userId,
  sessions,
  defaultSessionId,
  scoreField,
}: Props) {
  const [selectedScoreField, setSelectedScoreField] = useState(scoreField)
  const comparisonSessions = useMemo(
    () => getComparisonSessions(sessions),
    [sessions],
  )
  const initialSessionIds = useMemo(
    () => getInitialSessionIds(comparisonSessions, defaultSessionId),
    [comparisonSessions, defaultSessionId],
  )
  const [selectedSessionIds, setSelectedSessionIds] =
    useState<number[]>(initialSessionIds)
  const { data, isFetching, isError } =
    useGetGrowthWheelAssessmentComparisonQuery(
      {
        userId,
        assessmentSessionIds: selectedSessionIds,
        scoreField: selectedScoreField,
      },
      { skip: selectedSessionIds.length === 0 },
    )

  useEffect(() => {
    setSelectedSessionIds(current => {
      const validIds = current.filter(id =>
        comparisonSessions.some(session => session.id === id),
      )
      if (validIds.length > 0) return validIds.slice(0, 2)
      return initialSessionIds
    })
  }, [comparisonSessions, initialSessionIds])

  const chartSessions =
    data?.sessions?.length && selectedSessionIds.length
      ? data.sessions
      : comparisonSessions.filter(session =>
          selectedSessionIds.includes(session.id),
        )
  const competencies = data?.competencies ?? []
  const pointGroups = useMemo(
    () => buildPointGroups(chartSessions, competencies),
    [chartSessions, competencies],
  )
  const canCompare = comparisonSessions.length > 0

  const handlePrimarySessionChange = (sessionId: number) => {
    setSelectedSessionIds(current => {
      const secondary = current.find(id => id !== sessionId)
      return secondary ? [sessionId, secondary] : [sessionId]
    })
  }

  const handleSecondarySessionChange = (sessionId: string) => {
    setSelectedSessionIds(current => {
      const primary = current[0] ?? comparisonSessions[0]?.id
      if (!primary) return []
      if (!sessionId) return [primary]
      return [primary, Number(sessionId)].filter(
        (id, index, ids) => ids.indexOf(id) === index,
      )
    })
  }

  if (!canCompare) {
    return (
      <div className="card gw-growth-comparison-card">
        <div className="gw-growth-comparison-empty">
          No completed or active assessment cycles are available to compare.
        </div>
      </div>
    )
  }

  return (
    <div className="card gw-growth-comparison-card">
      <div className="gw-growth-comparison-header">
        <div>
          <h3 className="gw-growth-comparison-title">Competency Growth</h3>
          <p className="gw-growth-comparison-subtitle">
            Compare scores across assessment sessions.
          </p>
        </div>
        <GrowthComparisonChartControls
          comparisonSessions={comparisonSessions}
          selectedScoreField={selectedScoreField}
          selectedSessionIds={selectedSessionIds}
          onPrimarySessionChange={handlePrimarySessionChange}
          onScoreFieldChange={setSelectedScoreField}
          onSecondarySessionChange={handleSecondarySessionChange}
        />
      </div>

      {isError ? (
        <div className="alert alert-danger small mb-0">
          Could not load comparison data. Please try again.
        </div>
      ) : isFetching ? (
        <div className="gw-growth-comparison-empty">Loading comparison...</div>
      ) : competencies.length === 0 ? (
        <div className="gw-growth-comparison-empty">
          No competency scores are available for the selected cycle.
        </div>
      ) : (
        <GrowthComparisonChartCanvas
          competencies={competencies}
          pointGroups={pointGroups}
          sessions={chartSessions}
        />
      )}
    </div>
  )
}
